import { useEffect, useLayoutEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { selectUser, selectUserIsPremium, selectUserIsSuper } from '@/store/slices/user/selectors'
import { SignInManager } from '@/scenes/Editor/components/Navbar/components/SignInManager'
import { cancelLimitedTimeOffer, maybeShowLimitedTimeOfferForTrigger, preserveLimitedTimeOfferForProjectUrl } from './limitedTimeOffer'

let editorExitRequested = false
export function requestLimitedTimeOfferOnEditorExit(): void { editorExitRequested = true }

function isEditorPath(path: string): boolean {
  return path === '/' || /^\/design\/[^/]+\/edit(?:\/|$)/.test(path) || path === '/ai-studio'
}

export default function LimitedTimeOfferLifecycle() {
  const location = useLocation()
  const history = useHistory()
  const previousPath = useRef(location.pathname)
  const user = useSelector(selectUser)
  const userId = user?.userId ?? user?.id
  const premium = useSelector(selectUserIsPremium)
  const superTier = useSelector(selectUserIsSuper)

  useEffect(() => {
    if (!userId || !premium || superTier) return
    void SignInManager.getInstance().ensureLimitedTimeOfferSubscriptionFresh()
    const warm = () => { if (document.visibilityState === 'visible') void SignInManager.getInstance().ensureLimitedTimeOfferSubscriptionFresh() }
    window.addEventListener('focus', warm)
    document.addEventListener('visibilitychange', warm)
    return () => {
      window.removeEventListener('focus', warm)
      document.removeEventListener('visibilitychange', warm)
    }
  }, [userId, premium, superTier])

  useLayoutEffect(() => {
    const before = previousPath.current
    previousPath.current = location.pathname
    if (before === location.pathname) return
    const assignedProjectUrl = before === '/' && /^\/design\/[^/]+\/edit/.test(location.pathname) && history.action === 'REPLACE'
    if (assignedProjectUrl) preserveLimitedTimeOfferForProjectUrl(before, location.pathname)
    else cancelLimitedTimeOffer()
    const intentional = editorExitRequested || history.action === 'POP'
    editorExitRequested = false
    if (intentional && isEditorPath(before) && !isEditorPath(location.pathname)) {
      maybeShowLimitedTimeOfferForTrigger('editor-exit')
    }
  }, [location.pathname, history])

  useEffect(() => () => cancelLimitedTimeOffer(), [])
  return null
}
